import { useAuth } from "@dl/web-core-js/auth";
import { Button, Dialog, Field, MultiSelect, Select } from "@dl/web-core-js/ui";
import { useExport } from "@dl/web-core-js/shell";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { clientLabel, NexpimClientClient } from "../../api/pricing";
import {
  areMonthsConsecutive,
  MONTH_NAMES,
  ReportingClient,
  type MonthName,
} from "../../api/reporting";

export type FinanceReport = "sales" | "commissions" | "royalty" | "cogs";

interface Props {
  report: FinanceReport | null;
  onClose: () => void;
}

const monthLabel = (month: MonthName) =>
  new Date(2000, MONTH_NAMES.indexOf(month), 1).toLocaleString(undefined, { month: "long" });

function yearOptions(count: number) {
  const current = new Date().getFullYear();
  return Array.from({ length: count }, (_, index) => {
    const year = current - index;
    return { value: String(year), label: String(year) };
  });
}

/**
 * The four finance-report dialogs from the Vaadin product menu: the sales
 * workbook, commissions PDF, designer-royalty PDF and COGS workbook. All share
 * the year + consecutive-months pattern, so they live in one component keyed
 * by which report is open.
 */
export function FinanceReportDialogs({ report, onClose }: Props) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const { start: startExport, busy } = useExport();

  const reporting = useMemo(() => new ReportingClient(http), [http]);
  const clients = useMemo(() => new NexpimClientClient(http), [http]);

  const currentYear = new Date().getFullYear();
  const currentMonth = MONTH_NAMES[new Date().getMonth()]!;

  const [year, setYear] = useState(String(currentYear));
  const [months, setMonths] = useState<string[]>([]);
  const [clientId, setClientId] = useState("");
  const [designerId, setDesignerId] = useState("");
  const [includes, setIncludes] = useState({
    include_clients: true,
    include_products: true,
    include_refunds: true,
    include_invoices: false,
  });
  // COGS period defaults mirror Vaadin: from a year ago's current month, to now.
  const [cogsFrom, setCogsFrom] = useState({ year: String(currentYear - 1), month: currentMonth });
  const [cogsTo, setCogsTo] = useState({ year: String(currentYear), month: currentMonth });

  const clientListQuery = useQuery({
    queryKey: ["client", "last-order-2y"],
    queryFn: () => clients.byLastOrder(2),
    staleTime: 30 * 60_000,
    enabled: report === "sales",
  });

  const designersQuery = useQuery({
    queryKey: ["config", "designers"],
    queryFn: () =>
      http.get<Array<{ id: number; name?: string | null }>>("/product/designer"),
    staleTime: 30 * 60_000,
    enabled: report === "royalty",
  });

  const selectedMonths = months as MonthName[];
  const monthsValid = areMonthsConsecutive(selectedMonths);

  const monthOptions = MONTH_NAMES.map((month) => ({ value: month, label: monthLabel(month) }));

  // Java built the COGS range as [from-month day 1, day before to-month day 1].
  const cogsRange = useMemo(() => {
    const fromIndex = MONTH_NAMES.indexOf(cogsFrom.month);
    const toIndex = MONTH_NAMES.indexOf(cogsTo.month);
    const pad = (value: number) => String(value + 1).padStart(2, "0");
    const from = `${cogsFrom.year}-${pad(fromIndex)}-01`;
    const toDate = new Date(Number(cogsTo.year), toIndex, 0);
    const to = `${toDate.getFullYear()}-${String(toDate.getMonth() + 1).padStart(2, "0")}-${String(
      toDate.getDate(),
    ).padStart(2, "0")}`;
    return { from, to };
  }, [cogsFrom, cogsTo]);

  const run = () => {
    if (!report) return;
    const numericYear = Number(year);
    const action = {
      sales: () =>
        reporting.documentSales({
          year: numericYear,
          months: selectedMonths,
          clientId: clientId === "" ? null : Number(clientId),
          includeTotal: true,
          ...{
            includeClients: includes.include_clients,
            includeProducts: includes.include_products,
            includeRefunds: includes.include_refunds,
            includeInvoices: includes.include_invoices,
          },
        }),
      commissions: () => reporting.documentCommission(numericYear, selectedMonths),
      royalty: () => reporting.documentRoyalty(Number(designerId), numericYear, selectedMonths),
      cogs: () => reporting.documentCogs(cogsRange.from, cogsRange.to),
    }[report];
    onClose();
    void startExport(`report-${report}`, action, t("job.download.failed", "Download failed"));
  };

  const confirmDisabled =
    busy !== null ||
    (report === "royalty" && designerId === "") ||
    (report !== "cogs" && !monthsValid);

  const titles: Record<FinanceReport, string> = {
    sales: t("dialog.export-sales.title", "Export sales report"),
    commissions: t("dialog.export-commissions.title", "Export commissions"),
    royalty: t("dialog.export-designer-royalty.title", "Export designer royalty"),
    cogs: t("dialog.export-cogs.title", "Export COGS"),
  };

  const yearMonthFields = (
    <>
      <Field label={t("form.field.year", "Year")} htmlFor="report-year">
        <Select
          id="report-year"
          value={year}
          options={yearOptions(8)}
          onChange={(event) => setYear(event.target.value)}
          className="h-8 w-full"
        />
      </Field>
      <Field
        label={t("form.field.month", "Months")}
        htmlFor="report-months"
        error={monthsValid ? undefined : t("form.validation.field.months-consecutive", "Months must be consecutive")}
      >
        <MultiSelect
          id="report-months"
          options={monthOptions}
          value={months}
          onChange={setMonths}
          placeholder={t("form.field.month.placeholder", "All months")}
        />
      </Field>
    </>
  );

  const periodPicker = (
    label: string,
    value: { year: string; month: MonthName },
    onChange: (value: { year: string; month: MonthName }) => void,
  ) => (
    <div className="flex gap-2">
      <Field label={label} htmlFor={`cogs-${label}-year`}>
        <Select
          id={`cogs-${label}-year`}
          value={value.year}
          options={yearOptions(4)}
          onChange={(event) => onChange({ ...value, year: event.target.value })}
          className="h-8 w-28"
        />
      </Field>
      <Field label={t("form.field.month", "Month")} htmlFor={`cogs-${label}-month`}>
        <Select
          id={`cogs-${label}-month`}
          value={value.month}
          options={monthOptions}
          onChange={(event) => onChange({ ...value, month: event.target.value as MonthName })}
          className="h-8 w-36"
        />
      </Field>
    </div>
  );

  return (
    <Dialog
      open={report !== null}
      onClose={onClose}
      title={report ? titles[report] : ""}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button onClick={run} disabled={confirmDisabled}>
            {t("dialog.confirm.export", "Export")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {report !== "cogs" && yearMonthFields}

        {report === "sales" && (
          <>
            <Field label={t("form.field.client", "Client")} htmlFor="report-client">
              <Select
                id="report-client"
                value={clientId}
                placeholder={t("form.field.client.placeholder", "All clients")}
                options={(clientListQuery.data ?? []).map((row) => ({
                  value: String(row.cli_id),
                  label: clientLabel(row),
                }))}
                onChange={(event) => setClientId(event.target.value)}
                className="h-8 w-full"
              />
            </Field>
            <div className="flex flex-col gap-1.5 text-[13px]">
              <label className="flex items-center gap-2 text-ink-muted">
                <input type="checkbox" checked disabled className="size-3.5 accent-[var(--accent)]" />
                {t("form.field.include-total", "Include totals")}
              </label>
              {(
                [
                  ["include_clients", t("form.field.include-clients", "Include clients")],
                  ["include_products", t("form.field.include-products", "Include products")],
                  ["include_refunds", t("form.field.include-refunds", "Include refunds")],
                  ["include_invoices", t("form.field.include-invoices", "Include invoices")],
                ] as const
              ).map(([key, label]) => (
                <label key={key} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={includes[key]}
                    onChange={(event) => setIncludes({ ...includes, [key]: event.target.checked })}
                    className="size-3.5 accent-[var(--accent)]"
                  />
                  {label}
                </label>
              ))}
            </div>
          </>
        )}

        {report === "royalty" && (
          <Field label={t("form.field.designer", "Designer")} htmlFor="report-designer">
            <Select
              id="report-designer"
              value={designerId}
              placeholder={t("form.field.designer.placeholder", "Select designer")}
              options={(designersQuery.data ?? []).map((row) => ({
                value: String(row.id),
                label: row.name ?? `#${row.id}`,
              }))}
              onChange={(event) => setDesignerId(event.target.value)}
              className="h-8 w-full"
            />
          </Field>
        )}

        {report === "cogs" && (
          <>
            {periodPicker(t("form.field.year-from", "From"), cogsFrom, setCogsFrom)}
            {periodPicker(t("form.field.year-to", "To"), cogsTo, setCogsTo)}
            <p className="text-[12px] text-ink-muted">
              {cogsRange.from} → {cogsRange.to}
            </p>
          </>
        )}
      </div>
    </Dialog>
  );
}
